<template>
  <div id="users-management" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading multiple account users' data..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="invitationDialog"
        v-model="invitationDialog"
        max-width="750px"
        persistent
        @click:outside="invitationDialog = false"
        @keydown.esc="invitationDialog = false"
      >
        <i-user-invitation-dialog
          @cancel="invitationDialog = false"
          @confirm="confirmInvitation"
        />
      </v-dialog>

      <v-dialog
        v-if="rolesDialog"
        v-model="rolesDialog"
        max-width="400px"
        persistent
        @click:outside=";((rolesDialog = false), (editableUser = null))"
        @keydown.esc=";((rolesDialog = false), (editableUser = null))"
      >
        <i-user-roles-dialog
          :user="editableUser"
          @cancel=";((rolesDialog = false), (editableUser = null))"
          @confirm="confirmEditRoles"
        />
      </v-dialog>

      <v-dialog
        v-if="actionDialog"
        v-model="actionDialog"
        max-width="400px"
        persistent
        @click:outside="cancelAction"
        @keydown.esc="cancelAction"
      >
        <i-user-action-dialog
          :action="actionType"
          :dialog="actionDialog"
          :text="actionText"
          @cancel="cancelAction"
          @confirm="confirmAction"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row>
          <template v-if="userIsAllowedToEdit">
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="invitationDialog = true"
              >
                Invite User
              </v-btn>
            </v-col>
          </template>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="account/users-management"
              :instructionSection="instructionSection"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === USERS }"
              @click="selectedView = USERS"
              >Users</v-btn
            >
          </v-col>
          <v-col cols="6" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === INVITATIONS }"
              @click="selectedView = INVITATIONS"
              >Invitations</v-btn
            >
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" lg="8">
            <i-local-filter
              :filterPlaceholder="'Search Users by email'"
              @filter="localFilter"
            />
          </v-col>
        </v-row>

        <template>
          <v-row>
            <v-col cols="12">
              <template v-if="selectedView === USERS">
                <v-col
                  class="pa-0"
                  v-if="!loading && filteredUsers.length === 0"
                >
                  <i-empty-results
                    hideButton
                    :title="emptyFilteredResultsText"
                  />
                </v-col>

                <template v-else>
                  <template v-if="$vuetifyDisplay.smAndDown">
                    <v-col
                      class="mt-0 pa-0"
                      v-for="item in filteredUsers"
                      :key="item.email"
                    >
                      <v-card class="rounded-2 pa-4 mb-6">
                        <v-row no-gutters>
                          <v-col class="py-1">
                            <span
                              ><strong>{{ usersHeaders[0].text }}</strong></span
                            >
                            <br />
                            <span
                              ><strong>{{ item.email }}</strong></span
                            >
                          </v-col>
                        </v-row>

                        <v-divider class="my-2"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="6" class="py-1">
                            <span>{{ usersHeaders[1].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ $filters.text(item.firstName) }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ usersHeaders[2].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ $filters.text(item.lastName) }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ usersHeaders[3].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{
                              $filters.sentenceCase(item.department)
                            }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ usersHeaders[4].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ $filters.text(item.mobile) }}</span>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <span>{{ usersHeaders[5].text }}</span>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-chip
                              dense
                              outlined
                              small
                              class="mr-1"
                              color="primary"
                              v-for="role in item.roles"
                              :key="role"
                            >
                              {{ $filters.sentenceCase(role) }}
                            </v-chip>
                          </v-col>
                        </v-row>

                        <template v-if="!isOwner(item) && userIsAllowedToEdit">
                          <v-divider class="mt-2 mb-4"></v-divider>

                          <v-row no-gutters>
                            <v-col cols="12" class="py-1">
                              <v-btn
                                class="i-btn-actions-blue g-full-width"
                                @click="
                                  ;((editableUser = item), (rolesDialog = true))
                                "
                              >
                                <span>Edit User Roles</span>
                                <v-icon small class="ml-1"
                                  >mdi-circle-edit-outline</v-icon
                                >
                              </v-btn>
                            </v-col>
                            <v-col cols="12" class="py-1">
                              <v-btn
                                class="i-btn-actions-red g-full-width"
                                @click="
                                  action(
                                    item,
                                    'DELETE',
                                    'Are you sure you want to delete this user?'
                                  )
                                "
                              >
                                <span>Delete User</span>
                                <v-icon small class="ml-1"
                                  >mdi-delete-outline</v-icon
                                >
                              </v-btn>
                            </v-col>
                          </v-row>
                        </template>

                        <v-divider class="my-2"></v-divider>

                        <v-btn
                          rounded
                          class="g-full-width i-btn-block-primary"
                          @click="item.showDetails = !item.showDetails"
                        >
                          <span v-if="item.showDetails"
                            >Hide details<v-icon small right
                              >mdi-arrow-up</v-icon
                            ></span
                          >
                          <span v-else
                            >Show details<v-icon small right
                              >mdi-arrow-down</v-icon
                            ></span
                          >
                        </v-btn>

                        <v-col cols="12" v-if="item.showDetails">
                          <div class="pa-4 pb-2 g-heading-xs g-bold">
                            Email Preferences
                          </div>
                          <v-divider class="pa-0 ma-0"></v-divider>
                          <div class="i-email-preferences-div-emails">
                            <div
                              class="i-email-preferences-div-email"
                              v-for="email in visibleEmailPreferences(
                                item.emailPreferences
                              )"
                              :key="email.emailType"
                            >
                              <div
                                class="i-email-preferences-div-email-inner-mini"
                              >
                                <v-checkbox
                                  hide-details
                                  class="ma-0"
                                  v-model="email.receive"
                                  disabled
                                >
                                  <template v-slot:label>
                                    <div
                                      class="i-email-preferences-div-email-text"
                                    >
                                      <p class="g-bold g-heading-xxs">
                                        {{ email.emailTitle }}
                                      </p>
                                      <p>{{ email.emailDescription }}</p>
                                    </div>
                                  </template>
                                </v-checkbox>
                              </div>
                              <v-divider class="pa-0 ma-0"></v-divider>
                            </div>
                          </div>
                        </v-col>
                      </v-card>
                    </v-col>
                  </template>

                  <v-data-table
                    v-else
                    hide-default-footer
                    show-expand
                    class="pt-2 elevation-1 i-data-table"
                    item-key="email"
                    :headers="usersHeaders"
                    :items="filteredUsers"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`header.roles`]="{ header }">
                      {{ header.text }}
                      <v-icon
                        class="i-tooltip-color pl-1 pb-1"
                        small
                        v-tooltip="'Owner cannot be deleted / modified'"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>

                    <template #[`item.firstName`]="{ item }">
                      {{ $filters.text(item.firstName) }}
                    </template>

                    <template #[`item.lastName`]="{ item }">
                      {{ $filters.text(item.lastName) }}
                    </template>

                    <template #[`item.department`]="{ item }">
                      {{ $filters.sentenceCase(item.department) }}
                    </template>

                    <template #[`item.mobile`]="{ item }">
                      {{ $filters.text(item.mobile) }}
                    </template>

                    <template #[`item.roles`]="{ item }">
                      <v-chip
                        dense
                        outlined
                        small
                        class="mr-1 my-1"
                        color="primary"
                        v-for="role in item.roles"
                        :key="role"
                      >
                        {{ $filters.sentenceCase(role) }}
                      </v-chip>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        icon
                        color="secondary"
                        @click=";((editableUser = item), (rolesDialog = true))"
                        :disabled="isOwner(item) || !userIsAllowedToEdit"
                        v-tooltip="{
                          content: 'Edit user roles',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        icon
                        color="error"
                        @click="
                          action(
                            item,
                            'DELETE',
                            'Are you sure you want to delete this user?'
                          )
                        "
                        :disabled="isOwner(item) || !userIsAllowedToEdit"
                        v-tooltip="{
                          content: 'Delete user',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>

                    <template #expanded-item="{ headers, item }">
                      <td :colspan="headers.length">
                        <div class="pa-4 pb-2 g-heading-xs g-bold">
                          Email Preferences
                        </div>
                        <v-divider class="pa-0 ma-0"></v-divider>
                        <div class="i-email-preferences-div-emails">
                          <div
                            class="i-email-preferences-div-email"
                            v-for="email in visibleEmailPreferences(
                              item.emailPreferences
                            )"
                            :key="email.emailType"
                          >
                            <div
                              class="i-email-preferences-div-email-inner-mini"
                            >
                              <v-checkbox
                                hide-details
                                class="ma-0"
                                v-model="email.receive"
                                disabled
                              >
                                <template v-slot:label>
                                  <div
                                    class="i-email-preferences-div-email-text"
                                  >
                                    <p class="g-bold g-heading-xxs">
                                      {{ email.emailTitle }}
                                    </p>
                                    <p>{{ email.emailDescription }}</p>
                                  </div>
                                </template>
                              </v-checkbox>
                            </div>
                            <v-divider class="pa-0 ma-0"></v-divider>
                          </div>
                        </div>
                      </td>
                    </template>
                  </v-data-table>
                </template>
              </template>

              <template v-if="selectedView === INVITATIONS">
                <v-col
                  class="pa-0"
                  v-if="!loading && filteredInvitations.length === 0"
                >
                  <i-empty-results
                    hideButton
                    :title="emptyFilteredResultsText"
                  />
                </v-col>

                <template v-else>
                  <template v-if="$vuetifyDisplay.smAndDown">
                    <v-col
                      class="mt-0 pa-0"
                      v-for="item in filteredInvitations"
                      :key="item.id"
                    >
                      <v-card class="rounded-2 pa-4 mb-6">
                        <v-row no-gutters>
                          <v-col class="py-1">
                            <span
                              ><strong>{{
                                invitationsHeaders[0].text
                              }}</strong></span
                            >
                            <br />
                            <span
                              ><strong>{{ item.invitedEmail }}</strong></span
                            >
                          </v-col>
                        </v-row>

                        <v-divider class="my-2"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[1].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{
                              $filters.text(item.invitedFirstName)
                            }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[2].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{
                              $filters.text(item.invitedLastName)
                            }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[3].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{
                              $filters.sentenceCase(item.invitedDepartment)
                            }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[4].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ $filters.text(item.invitedMobile) }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[6].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ item.invitedBy }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[7].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{
                              $filters.formatDateTime12h(item.validUntil)
                            }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <span>{{ invitationsHeaders[5].text }}</span>
                          </v-col>
                          <v-col cols="6" class="py-1">
                            <v-chip
                              dense
                              outlined
                              small
                              class="mr-1 my-1"
                              color="primary"
                            >
                              {{ $filters.sentenceCase(item.invitedRole) }}
                            </v-chip>
                          </v-col>
                        </v-row>

                        <template v-if="userIsAllowedToEdit">
                          <v-divider class="my-2"></v-divider>

                          <v-row no-gutters>
                            <v-col cols="12" class="py-1">
                              <v-btn
                                class="i-btn-actions-blue g-full-width"
                                @click="
                                  action(
                                    item,
                                    'RESEND',
                                    'Would you like to re-send this invitation?'
                                  )
                                "
                              >
                                <span>Re-send Invitation</span>
                                <v-icon small class="ml-1"
                                  >mdi-email-fast-outline</v-icon
                                >
                              </v-btn>
                            </v-col>
                            <v-col cols="12" class="py-1">
                              <v-btn
                                class="i-btn-actions-red g-full-width"
                                @click="
                                  action(
                                    item,
                                    'REMOVE',
                                    'Are you sure you want to remove this invitation?'
                                  )
                                "
                              >
                                <span>Remove Invitation</span>
                                <v-icon small class="ml-1"
                                  >mdi-delete-outline</v-icon
                                >
                              </v-btn>
                            </v-col>
                          </v-row>
                        </template>
                      </v-card>
                    </v-col>
                  </template>

                  <v-data-table
                    v-else
                    class="pt-2 elevation-1 i-data-table"
                    hide-default-footer
                    item-key="id"
                    :headers="invitationsHeaders"
                    :items="filteredInvitations"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.invitedFirstName`]="{ item }">
                      {{ $filters.text(item.invitedFirstName) }}
                    </template>

                    <template #[`item.invitedLastName`]="{ item }">
                      {{ $filters.text(item.invitedLastName) }}
                    </template>

                    <template #[`item.invitedDepartment`]="{ item }">
                      {{ $filters.sentenceCase(item.invitedDepartment) }}
                    </template>

                    <template #[`item.invitedMobile`]="{ item }">
                      {{ $filters.text(item.invitedMobile) }}
                    </template>

                    <template #[`item.invitedRole`]="{ item }">
                      <v-chip
                        dense
                        outlined
                        small
                        class="mr-1 my-1"
                        color="primary"
                      >
                        {{ $filters.sentenceCase(item.invitedRole) }}
                      </v-chip>
                    </template>

                    <template #[`item.validUntil`]="{ item }">
                      {{ $filters.formatDateTime12h(item.validUntil) }}
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        icon
                        color="secondary"
                        @click="
                          action(
                            item,
                            'RESEND',
                            'Would you like to re-send this invitation?'
                          )
                        "
                        :disabled="!userIsAllowedToEdit"
                        v-tooltip="{
                          content: 'Re-send invitation',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-email-fast-outline</v-icon>
                      </v-btn>

                      <v-btn
                        icon
                        color="error"
                        @click="
                          action(
                            item,
                            'REMOVE',
                            'Are you sure you want to remove this invitation?'
                          )
                        "
                        :disabled="!userIsAllowedToEdit"
                        v-tooltip="{
                          content: 'Remove invitation',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { fetchUsers, deleteUser } from '@/services/userAccess'
import {
  cancelInvitation,
  fetchInvitations,
  resendInvitationEmail
} from '@/services/invitation'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import iUserActionDialog from '../components/UserActionDialog.vue'
import iUserInvitationDialog from '../components/UserInvitationDialog.vue'
import iUserRolesDialog from '../components/UserRolesDialog.vue'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

const VIEWS = {
  USERS: 'USERS',
  INVITATIONS: 'INVITATIONS'
}

export default {
  name: 'users-management',

  components: {
    iEmptyResults,
    iInstructions,
    iLocalFilter,
    iOverlay,
    iUserActionDialog,
    iUserInvitationDialog,
    iUserRolesDialog
  },

  data: () => ({
    actionData: null,
    actionDialog: false,
    actionText: null,
    actionType: null,
    editableUser: null,
    loading: true,
    invitationDialog: false,
    invitations: [],
    rolesDialog: false,
    filterText: '',
    selectedView: VIEWS.USERS,
    mappedUsers: [],
    usersHeaders: [
      { text: 'EMAIL', value: 'email', width: '25%' },
      { text: 'FIRST NAME', value: 'firstName', width: '13%' },
      { text: 'LAST NAME', value: 'lastName', width: '13%' },
      { text: 'DEPARTMENT', value: 'department', width: '13%' },
      { text: 'CONTACT', value: 'mobile', width: '12%' },
      { text: 'ROLE(S)', value: 'roles', sortable: false, width: '12%' },
      {
        text: 'ACTIONS',
        value: 'actions',
        sortable: false,
        align: 'center',
        width: 150
      }
    ],
    invitationsHeaders: [
      { text: 'EMAIL', value: 'invitedEmail', width: '18%' },
      { text: 'FIRST NAME', value: 'invitedFirstName', width: '12%' },
      { text: 'LAST NAME', value: 'invitedLastName', width: '12%' },
      { text: 'DEPARTMENT', value: 'invitedDepartment', width: '9%' },
      { text: 'CONTACT', value: 'invitedMobile', width: '9%' },
      { text: 'ROLE', value: 'invitedRole', width: '9%' },
      { text: 'INVITED BY', value: 'invitedBy', width: '18%' },
      { text: 'INVITATION VALID UNTIL', value: 'validUntil', width: '10%' },
      {
        text: 'ACTIONS',
        value: 'actions',
        sortable: false,
        align: 'center',
        width: 150
      }
    ],
    ...VIEWS
  }),

  computed: {
    ...mapGetters('preferences', ['isOwnerUser', 'isAdminUser']),
    ...mapGetters('tenant', ['orderChatEnabled']),
    ...mapState('preferences', ['selectedSupplierId']),

    instructionSection() {
      let view = null
      switch (this.selectedView) {
        case VIEWS.USERS:
          view = 'Users'
          break
        case VIEWS.INVITATIONS:
          view = 'Invitations'
          break
      }
      return view
    },

    emptyFilteredResultsText() {
      return this.selectedView === VIEWS.USERS
        ? 'No registered users found'
        : 'No user invitations found'
    },

    filteredUsers() {
      return this.mappedUsers.filter((user) =>
        user.email.includes(this.filterText.toLowerCase())
      )
    },

    filteredInvitations() {
      return this.invitations.filter((invitation) =>
        invitation.invitedEmail.includes(this.filterText.toLowerCase())
      )
    },

    userIsAllowedToEdit() {
      return this.isOwnerUser || this.isAdminUser
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    visibleEmailPreferences(preferences) {
      return (preferences || []).filter(
        (email) =>
          email.emailType !== 'ORDER_CHAT_UNREAD_REMINDER' ||
          this.orderChatEnabled
      )
    },

    async fetchData() {
      try {
        const users = await fetchUsers(this.selectedSupplierId)
        this.mappedUsers = this.mapUsers(users)
        this.invitations = await fetchInvitations(this.selectedSupplierId)
        this.loading = false
      } catch (err) {
        this.addNotification({ color: 'error', defaultError: true })
      }
    },

    action(actionData, actionType, actionText) {
      this.actionData = actionData
      this.actionType = actionType
      this.actionText = actionText
      this.actionDialog = true
    },

    cancelAction() {
      this.actionDialog = false
      this.actionType = this.actionText = this.actionData = null
    },

    confirmAction() {
      if (this.actionType === 'DELETE') this.handleDeleteUser(this.actionData)
      if (this.actionType === 'RESEND')
        this.handleResendInvitation(this.actionData)
      if (this.actionType === 'REMOVE')
        this.handleRemoveInvitation(this.actionData)
      this.cancelAction()
    },

    confirmEditRoles() {
      this.rolesDialog = false
      this.editableUser = null
      this.fetchData()
    },

    confirmInvitation() {
      this.invitationDialog = false
      this.fetchData()
    },

    handleDeleteUser(user) {
      this.loading = true
      deleteUser(this.selectedSupplierId, user.email)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'User deleted!'
          })
          this.fetchData()
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'User could not be deleted...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleResendInvitation(invitation) {
      this.loading = true
      resendInvitationEmail(this.selectedSupplierId, invitation.token)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'User invitation re-sent!'
          })
          this.fetchData()
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'User invitation could not be re-sent...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleRemoveInvitation(invitation) {
      this.loading = true
      cancelInvitation(invitation.id)
        .then(() => {
          this.fetchData()
          this.addNotification({
            color: 'secondary',
            text: 'User invitation deleted!'
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'User invitation could not be deleted...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    isOwner(user) {
      return user.roles.includes('OWNER')
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    mapUsers(users) {
      return users.map((user) => {
        return { ...user, showDetails: false }
      })
    },

    roleSentenceCase(role) {
      switch (role) {
        case 'OWNER':
          role = 'Owner'
          break
        case 'ADMIN':
          role = 'Admin'
          break
        case 'AGENT':
          role = 'Agent'
          break
        default:
          role = 'Standard'
      }
      return role
    }
  },

  mounted() {
    this.fetchData()
  }
}
</script>

<style lang="scss" scoped>
// #users-management {}
</style>
